import React, { memo, useCallback, useEffect } from "react";
import ReactPaginate from "react-paginate";
import PropTypes from "prop-types";
import {
  stringifyUrl,
  isPageForSeo,
  installGtmPagerHijackGuard,
} from "../../../../utils/search";
import ArticleSearchContainer from "../../../containers/ArticleSearchContainer";

const calcPageRangeDisplayed = (page, totalPages) => {
  if (totalPages <= 5) {
    return totalPages;
  }
  if (page == 1 || page == totalPages) {
    return 4;
  }
  return 5;
};

const resultsPerPage = 30;

const PagingInfo = memo(({ page, total }) => {
  const currentPage = page === 0 ? 1 : page;
  const start =
    currentPage > 1 ? (currentPage - 1) * resultsPerPage + 1 : currentPage;
  const end =
    currentPage * resultsPerPage > total ? total : currentPage * resultsPerPage;
  return (
    total > 0 &&
    start <= end && (
      <h2 className="m_pagingInfo">
        <span className="mainHeadingAllNumber">{total}</span>
        件中
        <span className="mainHeadingNumber">
          {start}-{end}
        </span>
        を表示
      </h2>
    )
  );
});

// HACK: propsバリデーションでエラーになるため、一時的にコメントアウト
// PagingInfo.propTypes = {
//   page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
//   total: PropTypes.number.isRequired,
// };

const JumpBtn = memo(({ handlePageClick, targetPage, title, className }) => (
  <button
    type="button"
    onClick={() => handlePageClick({ selected: targetPage })}
    className={className}
  >
    <span>{title}</span>
  </button>
));

JumpBtn.propTypes = {
  handlePageClick: PropTypes.func.isRequired,
  targetPage: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
};

const Paging = memo(({ totalPages, page, handlePageClick, hrefBuilder }) => {
  const pageRangeDisplayed = calcPageRangeDisplayed(page, totalPages);
  return (
    totalPages > 1 &&
    page <= totalPages && (
      <div className="isTransparent m_reactPagenaviZone jsPagenaviZone">
        {page !== 1 && (
          <JumpBtn
            handlePageClick={handlePageClick}
            targetPage={0}
            title="最初へ"
            className="pagenaviZoneFirst"
          />
        )}
        <ReactPaginate
          previousLabel="前へ"
          nextLabel="次へ"
          breakLabel=""
          pageCount={totalPages}
          marginPagesDisplayed={1}
          pageRangeDisplayed={pageRangeDisplayed}
          disableInitialCallback
          onPageChange={handlePageClick}
          containerClassName="mainPagenavi"
          pageClassName="page"
          activeClassName="current"
          previousClassName="pagenaviBtn"
          nextClassName="pagenaviBtn"
          previousLinkClassName={`p_btn ${page === 1 ? "isDisable" : "isSub"
            } previouspostslink`}
          nextLinkClassName={`p_btn ${page === totalPages ? "isDisable" : "isSub"
            } nextpostslink`}
          initialPage={page - 1}
          forcePage={page - 1}
          disabledClassName=""
          hrefBuilder={hrefBuilder}
        />
        {page !== totalPages && (
          <JumpBtn
            handlePageClick={handlePageClick}
            targetPage={totalPages - 1}
            title="最後へ"
            className="pagenaviZoneLast"
          />
        )}
      </div>
    )
  );
});

Paging.propTypes = {
  totalPages: PropTypes.number.isRequired,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handlePageClick: PropTypes.func.isRequired,
  hrefBuilder: PropTypes.func.isRequired,
};

export function OnlyPager({
  children,
  totalPages,
  page,
  locationPath,
  anchorLinkId,
}) {
  const { requestParam } = ArticleSearchContainer.useContainer();

  // NOTE: GTM の Wait-for-Tags リンククリックがページャー遷移を乗っ取る不具合への対策。
  //       OnlyPager は PagingZone 非同居のページ(企業ポータルの topics 等)でも使われるため、ここでも設置する。
  useEffect(() => installGtmPagerHijackGuard(), []);

  const hrefBuilder = (newPage) => {
    const { ...newState } = requestParam;
    const search = stringifyUrl("", newState);
    const notIncludeSearchresultInUrl = !search || search.match(/\?page=\d*$/);
    const pageNum = notIncludeSearchresultInUrl ? /\?page=\d*/ : /&page=\d*/;

    let href;
    if (newPage === 1) {
      // NOTE: canonical URL自己参照のため、1ページ目の場合はpageパラメータを除去
      const urlWithoutPage = locationPath + search.replace(/[?&]page=\d+/, '').replace(/^&/, '?');
      href = urlWithoutPage.endsWith('?') ? urlWithoutPage.slice(0, -1) : urlWithoutPage;
    } else {
      const addPage = notIncludeSearchresultInUrl
        ? `?page=${newPage}`
        : `&page=${newPage}`;
      const url = locationPath + search;
      const newUrl = url.match(pageNum)
        ? url.replace(pageNum, addPage)
        : url + addPage;
      href = locationPath.includes("searchresult")
        ? newUrl
        : `${locationPath}?page=${newPage}`;
    }

    if (anchorLinkId && !href.includes(`#${anchorLinkId}`)) {
      href = `${href}#${anchorLinkId}`;
    }
    return href;
  };

  const handlePageClick = (selectedItem) => {
    const currentPage = selectedItem.selected + 1;
    const url = hrefBuilder(currentPage);
    window.location.href = url;
  };

  return (
    <div>
      {children}
      <Paging
        totalPages={totalPages}
        page={page}
        handlePageClick={handlePageClick}
        hrefBuilder={hrefBuilder}
      />
    </div>
  );
}

OnlyPager.propTypes = {
  children: PropTypes.element.isRequired,
  totalPages: PropTypes.number.isRequired,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  locationPath: PropTypes.string.isRequired,
  anchorLinkId: PropTypes.string.isRequired,
};

function PagingZone({ children }) {
  const {
    requestParam,
    total,
    page,
    totalPages,
    initialLocationPath,
    switchPageSearch,
    lastPageUrlRefreshedAt,
  } = ArticleSearchContainer.useContainer();

  // NOTE: GTM の Wait-for-Tags リンククリックがページャー遷移を乗っ取る不具合への対策
  useEffect(() => installGtmPagerHijackGuard(), []);

  const handlePageClick = useCallback(
    (selectedItem) => {
      const currentPage = selectedItem.selected;
      switchPageSearch(currentPage + 1);
    }, [lastPageUrlRefreshedAt]
  );

  const hrefBuilder = useCallback(
    (newPage) => {
      const { ...newState } = requestParam;
      delete newState.page;
      if (newPage != 1) {
        newState.page = newPage;
      }
      const baseUrl = initialLocationPath || location.pathname;

      const hrefState = isPageForSeo(baseUrl)
        ? { page: newState.page }
        : newState;
      return stringifyUrl(baseUrl, hrefState);
    }, [lastPageUrlRefreshedAt]
  );

  return (
    <>
      <PagingInfo page={page} total={total} />
      <Paging
        totalPages={totalPages}
        page={page}
        handlePageClick={handlePageClick}
        hrefBuilder={hrefBuilder}
      />
      {children}
      <Paging
        totalPages={totalPages}
        page={page}
        handlePageClick={handlePageClick}
        hrefBuilder={hrefBuilder}
      />
      {total > resultsPerPage && <PagingInfo page={page} total={total} />}
    </>
  );
}

// HACK: propsバリデーションでエラーになるため、一時的にコメントアウト
// PagingZone.propTypes = {
//   // TODO: 適切な型指定が必要
//   children: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
// };

export default PagingZone;
